import { mskDateString } from "@/lib/refresh/dates";

export const ACTION_DIARY_DAY_PREFIX = "action_diary_day:";
export const ACTION_DIARY_EDITOR_COLUMN_KEY = "action_diary";
export const ACTION_DIARY_DEFAULT_DAY_COUNT = 14;
export const ACTION_DIARY_DAY_COLUMN_DEFAULT_WIDTH = 85;
export const ACTION_DIARY_DAY_COLUMN_MIN_WIDTH = 48;

/**
 * На сколько полоса длящегося события вылезает за края ячейки дня, px.
 *
 * События вроде «в рекламе» или «нет остатков» длятся несколько дней, а колонок
 * — четырнадцать. Чтобы читалось одной непрерывной линией, каждый кусочек полосы
 * вылезает за края своей ячейки и перекрывает промежуток до соседа
 * (durationDiaryStyle в ActionDiaryCell).
 */
export const ACTION_DIARY_DURATION_BRIDGE_PX = 12;

/**
 * Боковые отступы ячейки дня под ширину перемычки.
 *
 * Промежуток между содержимым соседних дней должен ровно равняться перемычке:
 * больше — линия рвётся на просветы, меньше — куски наезжают друг на друга.
 * Часть промежутка может давать сама сетка (`gridGapX`), остальное добираем
 * отступами. Именно это и разъехалось в таблице новой версии: там ячейка имела
 * свои 8px, промежуток выходил 16px, и полоса до соседа не доставала.
 */
export function actionDiaryDayCellPaddingX(gridGapX: number): number {
  return Math.max(0, (ACTION_DIARY_DURATION_BRIDGE_PX - gridGapX) / 2);
}

export function actionDiaryDayColumnKey(dayOffset: number): string {
  return `${ACTION_DIARY_DAY_PREFIX}${Math.max(0, Math.floor(dayOffset))}`;
}

export function isActionDiaryDayColumnKey(key: string): boolean {
  return parseActionDiaryDayOffset(key) !== null;
}

export function isActionDiaryColumnKey(key: string): boolean {
  return key === ACTION_DIARY_EDITOR_COLUMN_KEY || isActionDiaryDayColumnKey(key);
}

export function parseActionDiaryDayOffset(key: string): number | null {
  if (!key.startsWith(ACTION_DIARY_DAY_PREFIX)) return null;
  const raw = key.slice(ACTION_DIARY_DAY_PREFIX.length);
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value >= 0 && value <= 60 ? value : null;
}

export function actionDiaryDateForOffset(dayOffset: number, nowMs = Date.now()): string {
  return mskDateString(nowMs - Math.max(0, Math.floor(dayOffset)) * 86400_000);
}

export function actionDiaryDayLabel(key: string, nowMs = Date.now()): string {
  const offset = parseActionDiaryDayOffset(key);
  if (offset == null) return key;
  if (offset === 0) return "Сегодня";
  const date = actionDiaryDateForOffset(offset, nowMs);
  const [, month, day] = date.split("-");
  return day && month ? `${day}.${month}` : date;
}

export function actionDiaryDayFullLabel(key: string, nowMs = Date.now()): string {
  const offset = parseActionDiaryDayOffset(key);
  if (offset == null) return key;
  return `Дневник ${actionDiaryDayLabel(key, nowMs)}`;
}

export function actionDiaryDayColumnKeys(days = ACTION_DIARY_DEFAULT_DAY_COUNT): string[] {
  const count = Math.max(1, Math.min(60, Math.floor(days)));
  return Array.from({ length: count }, (_, index) => actionDiaryDayColumnKey(index));
}

export function actionDiaryDayColumnKeysInOrder(columns: string[]): string[] {
  return [...new Set(columns.filter(isActionDiaryDayColumnKey))]
    .sort((a, b) => (parseActionDiaryDayOffset(a) ?? 0) - (parseActionDiaryDayOffset(b) ?? 0));
}

export function collapseActionDiaryColumnKeysForEditor(columns: string[]): string[] {
  const out: string[] = [];
  let diaryAdded = false;

  for (const key of columns) {
    if (isActionDiaryColumnKey(key)) {
      if (!diaryAdded) {
        out.push(ACTION_DIARY_EDITOR_COLUMN_KEY);
        diaryAdded = true;
      }
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }

  return out;
}

export function expandActionDiaryEditorColumnKeysForSave(
  columns: string[],
  previousColumns: string[] = [],
  days = ACTION_DIARY_DEFAULT_DAY_COUNT,
): string[] {
  const previousDiaryColumns = actionDiaryDayColumnKeysInOrder(previousColumns);
  const diaryColumns = previousDiaryColumns.length > 0 ? previousDiaryColumns : actionDiaryDayColumnKeys(days);
  const out: string[] = [];

  for (const key of columns) {
    if (key === ACTION_DIARY_EDITOR_COLUMN_KEY) {
      for (const diaryKey of diaryColumns) {
        if (!out.includes(diaryKey)) out.push(diaryKey);
      }
      continue;
    }
    if (isActionDiaryDayColumnKey(key)) {
      if (!out.includes(key)) out.push(key);
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }

  return out;
}

export function normalizeActionDiaryColumnOrder(columns: string[], days = ACTION_DIARY_DEFAULT_DAY_COUNT): string[] {
  const out: string[] = [];
  const diary = new Set<string>();
  let insertAt = -1;

  for (const key of columns) {
    if (key === ACTION_DIARY_EDITOR_COLUMN_KEY) {
      if (insertAt < 0) insertAt = out.length;
      for (const diaryKey of actionDiaryDayColumnKeys(days)) diary.add(diaryKey);
      continue;
    }
    if (isActionDiaryDayColumnKey(key)) {
      if (insertAt < 0) insertAt = out.length;
      diary.add(key);
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }

  if (insertAt < 0) return out;
  const diaryColumns = [...diary].sort((a, b) => (parseActionDiaryDayOffset(a) ?? 0) - (parseActionDiaryDayOffset(b) ?? 0));
  return [...out.slice(0, insertAt), ...diaryColumns, ...out.slice(insertAt)];
}

/**
 * Пункт «Дневник по дням» в редакторе колонок таблицы новой версии.
 *
 * В таблице дневник — N отдельных колонок по дням, а в редакторе они должны
 * быть одним пунктом: четырнадцать чипов «Сегодня», «12.09», «11.09»… в ленте
 * выбранных колонок не читались и мешали двигать остальные. Ключ — только для
 * редактора, в блок страницы он не пишется: перед сохранением разворачивается
 * обратно в дневные колонки (expandActionDiaryDaysEditorKey).
 *
 * Отличается от ACTION_DIARY_EDITOR_COLUMN_KEY («action_diary»): в новой
 * таблице это настоящая отдельная колонка с полосой дней в одной ячейке, и
 * смешивать её с дневными нельзя.
 */
export const ACTION_DIARY_DAYS_EDITOR_KEY = "action_diary_days";

/** Дневные колонки → один пункт редактора на месте первой из них. */
export function collapseActionDiaryDayColumns(columns: string[]): string[] {
  const out: string[] = [];
  let collapsed = false;
  for (const key of columns) {
    if (isActionDiaryDayColumnKey(key)) {
      if (!collapsed) {
        out.push(ACTION_DIARY_DAYS_EDITOR_KEY);
        collapsed = true;
      }
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

/**
 * Пункт редактора → дневные колонки блока. Набор дней берём из сохранённых
 * колонок (previousColumns), чтобы перестановка пункта не меняла число дней;
 * если дней ещё не было — стандартные 14. Явные дневные ключи в списке
 * (например, из другого вида) остаются на своих местах.
 */
export function expandActionDiaryDaysEditorKey(
  columns: string[],
  previousColumns: string[] = [],
  days = ACTION_DIARY_DEFAULT_DAY_COUNT,
): string[] {
  const previousDays = actionDiaryDayColumnKeysInOrder(previousColumns);
  const dayColumns = previousDays.length > 0 ? previousDays : actionDiaryDayColumnKeys(days);
  const out: string[] = [];
  for (const key of columns) {
    if (key === ACTION_DIARY_DAYS_EDITOR_KEY) {
      for (const dayKey of dayColumns) if (!out.includes(dayKey)) out.push(dayKey);
      continue;
    }
    if (!out.includes(key)) out.push(key);
  }
  return out;
}
